/* eslint-disable react-refresh/only-export-components */
"use client";

import { useEffect, useRef, useState } from "react";
import { X, AlertCircle, AlertTriangle, Info } from "lucide-react";
import type { ErrorNotification } from "@/context/error-provider";
import { cn } from "@/lib/utils";

export enum ErrorNotificationType {
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
}

type ErrorNotificationProps = {
  error: ErrorNotification;
  onDismiss: (id: string) => void;
};

export function ErrorNotificationComponent({
  error,
  onDismiss,
}: ErrorNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (error.duration && error.duration > 0) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev - 100 / (error.duration! / 100);
          return newProgress <= 0 ? 0 : newProgress;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [error.duration]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(error.id), 300); // Wait for exit animation
  };

  const getIcon = () => {
    switch (error.type) {
      case ErrorNotificationType.WARNING:
        return <AlertTriangle className="size-5" />;
      case ErrorNotificationType.INFO:
        return <Info className="size-5" />;
      default:
        return <AlertCircle className="size-5" />;
    }
  };

  const getColorClasses = () => {
    switch (error.type) {
      case ErrorNotificationType.WARNING:
        return "bg-yellow-50 border-yellow-200 text-yellow-800";
      case ErrorNotificationType.INFO:
        return "bg-blue-50 border-blue-200 text-blue-800";
      default:
        return "bg-red-50 border-red-200 text-red-800";
    }
  };

  const getProgressColor = () => {
    switch (error.type) {
      case ErrorNotificationType.WARNING:
        return "bg-yellow-400";
      case ErrorNotificationType.INFO:
        return "bg-blue-400";
      default:
        return "bg-red-400";
    }
  };

  // 4.1.3 Status Messages — AA: nothing here previously announced to a screen reader —
  // no role, no aria-live. Errors interrupt (role="alert" is reliably announced on
  // insertion, even though this container mounts already containing its text — that is
  // the documented exception to "the live region must pre-exist empty"); warning/info
  // are announced politely instead of interrupting whatever the user is doing.
  const isError = error.type === ErrorNotificationType.ERROR;
  const dismissButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // 2.4.3 Focus Order — A: focus the Dismiss button, not the alert container itself.
    // The container has no accessible name of its own, so focusing it reads its entire
    // text content as the name — which duplicated the message the live region below
    // announces on insertion. The Dismiss button has its own distinct name ("Dismiss
    // notification") and gives the member an immediate, actionable control instead of
    // just parking focus on inert text.
    //
    // The delay matters: moving focus in the same tick as the live-region insertion
    // makes VoiceOver read the alert twice — it starts announcing the region, then the
    // focus change makes it re-enter/re-announce that same region before reading the
    // focused control. Letting the live-region announcement start first and settle,
    // then moving focus, avoids the collision. Confirmed by reproducing both call
    // paths (a plain mutation failure and the group-code form submit) and checking:
    // there really is only one alert element each time — this is a timing collision
    // between two correct mechanisms reading the same element, not a duplicate node.
    const timer = setTimeout(() => dismissButtonRef.current?.focus(), 150);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      className={cn(
        "relative overflow-hidden rounded-lg border p-4 shadow-lg transition-all duration-300 ease-in-out",
        "transform",
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0",
        getColorClasses(),
      )}
      style={{ minWidth: "320px", maxWidth: "400px" }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0" aria-hidden="true">
          {getIcon()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="break-words text-sm font-medium">{error.message}</p>
        </div>
        <button
          ref={dismissButtonRef}
          onClick={handleDismiss}
          // focus:ring (not focus-visible:) — this button is focused programmatically
          // on mount, not via Tab, and :focus-visible's keyboard-only heuristic is
          // inconsistent for that across browsers. The ring must show every time so a
          // sighted keyboard user can see where focus landed.
          className="flex-shrink-0 rounded-md p-1.5 transition-colors hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          aria-label="Dismiss notification"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* Progress bar for auto-dismiss */}
      {error.duration && error.duration > 0 && (
        <div className="absolute inset-x-0 bottom-0 h-1 bg-black/10">
          <div
            className={cn(
              "h-full transition-all duration-100 ease-linear",
              getProgressColor(),
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
